import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector, touch } from 'redux-form'
// view components
import Column from './components/Column'
import ColumnForm from './components/ColumnForm'
// views
import { AddButton, DraggableWrapper } from 'components/Modals/PlanColumnsSettingsModal/views'
// constants
import { Forms } from 'constants/index'
import { PLAN_COLUMN_OPTIONS } from 'constants/planColumns'
// types
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray'
import { IPlanColumn, IPlanColumnDraft } from 'types/plan/columns'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
// utils
import { getOptionByValue, getValueFromOption } from 'utils/index'

interface Props {
  isDisabled: boolean
}

const selector = formValueSelector(Forms.PLAN_SETTINGS_FORM)

// Plan-settings columns list — the FieldArray holds a single draft entry that doubles as the
// create form and the edit form for an existing column.
const CustomColumns: React.FC<WrappedFieldArrayProps<any> & Props> = ({ fields, isDisabled }) => {
  // Which column the draft form is editing — kept in local state because FieldArray only
  // re-renders on length changes, not on value changes inside the array.
  const [editedColumnId, setEditedColumnId] = React.useState<string>(null)

  const dispatch = useDispatch()

  const columns: IPlanColumn[] = useSelector((state) => selector(state, 'columns')) || []

  // Column order is the order of this array — nothing else to keep in sync.
  const changeField = (value: IPlanColumn[]) => dispatch(change(Forms.PLAN_SETTINGS_FORM, 'columns', value))

  const removeColumn = (fieldId: string) => () => {
    changeField(columns.filter((column) => column.fieldId !== fieldId))
  }

  const reorder = (list: IPlanColumn[], startIndex: number, endIndex: number) => {
    const reorderedList = [...list]

    const [removed] = reorderedList.splice(startIndex, 1)
    reorderedList.splice(endIndex, 0, removed)
    return reorderedList
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const reorderedColumns = reorder(columns, source.index, destination.index)

      changeField(reorderedColumns)
    }
  }

  // A single draft entry lives in the FieldArray: it is rendered in place of the edited column,
  // or below the list when a new column is being added.
  const draft: IPlanColumnDraft = useSelector((state) => selector(state, fields.name))?.[0] || null
  const draftName = `${fields.name}[0]`

  const changeDraft = (value: IPlanColumnDraft[]) => dispatch(change(Forms.PLAN_SETTINGS_FORM, fields.name, value))

  const removeDraft = () => {
    setEditedColumnId(null)
    changeDraft([])
  }

  const addField = () => {
    setEditedColumnId(null)
    changeDraft([{}])
  }

  // Load an existing column into the draft form (replaces whatever draft is open).
  const editColumn = (column: IPlanColumn) => () => {
    setEditedColumnId(column.fieldId)
    changeDraft([
      { fieldId: column.fieldId, field: getOptionByValue(column.fieldId, PLAN_COLUMN_OPTIONS), width: column.width }
    ])
  }

  const saveColumn = () => {
    dispatch(touch(Forms.PLAN_SETTINGS_FORM, `${draftName}.field`))

    if (!draft?.field) {
      return
    }

    const width = Number(draft.width)
    const column: IPlanColumn = { fieldId: getValueFromOption(draft.field) }

    if (Number.isFinite(width) && width > 0) {
      column.width = width
    }

    changeField(
      editedColumnId ? columns.map((item) => (item.fieldId === editedColumnId ? column : item)) : [...columns, column]
    )
    removeDraft()
  }

  // Falls back to the bottom form if the edited column is gone (e.g. it was removed meanwhile).
  const isEditingExisting = !!editedColumnId && columns.some(({ fieldId }) => fieldId === editedColumnId)

  // Columns already in the list are dropped from the select — except the one being edited.
  const availableOptions = PLAN_COLUMN_OPTIONS.filter(
    ({ value }) => value === editedColumnId || !columns.some(({ fieldId }) => fieldId === value)
  )

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={'PLAN_SETTINGS_COLUMNS'}>
          {(provided) => (
            <div ref={provided.innerRef}>
              {columns.map((column, index) => {
                const isEdited = isEditingExisting && editedColumnId === column.fieldId

                return (
                  <Draggable
                    key={column.fieldId}
                    index={index}
                    draggableId={column.fieldId}
                    isDragDisabled={isDisabled || isEdited}>
                    {(provided) => (
                      <DraggableWrapper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...(provided.dragHandleProps || {})}
                        style={provided.draggableProps.style as React.CSSProperties}>
                        {isEdited ? (
                          <ColumnForm
                            name={draftName}
                            options={availableOptions}
                            onSave={saveColumn}
                            onCancel={removeDraft}
                          />
                        ) : (
                          <Column
                            column={column}
                            isDisabled={isDisabled}
                            editColumn={editColumn(column)}
                            removeColumn={removeColumn(column.fieldId)}
                          />
                        )}
                      </DraggableWrapper>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {draft && !isEditingExisting && (
        <ColumnForm name={draftName} options={availableOptions} onSave={saveColumn} onCancel={removeDraft} />
      )}
      {!isDisabled && (
        <AddButton $disabled={!!draft || !availableOptions.length} onClick={addField}>
          + add column
        </AddButton>
      )}
    </>
  )
}

export default CustomColumns
